/* eslint-disable new-cap */
import { LazyQueryExecFunction } from '@apollo/client';
import { LegacyUser } from '@website/types/LegacyUser';
import { DateTime } from 'luxon';
import { LegacyGroup } from '../../../types/LegacyGroup';
import {
  Currency,
  QueryTrustMyTravelAuthorisationCodeArgs,
  TransactionAddressInput,
  Trip,
  TrustMyTravelAuthorisationCodeQuery,
} from '../../../types/graphqlOperations';

/**
 * Format the modal options
 */
export function formatModalOptions({
  currency,
  endDate,
  trip,
  user,
  authorisation,
  values,
  env = process.env.NEXT_PUBLIC_ENVIRONMENT as 'prod' | 'dev',
}) {
  const res = {
    path: env === 'prod' ? 'skyhook-adventure' : 'skyhook-sandbox',
    transactionType: 'authorize',
    disableLang: true,
    data: {
      booking_id: '0', // Setting 0 makes TMT create this
      channels: authorisation.trustMyTravelAuthorisationCode.channelID,
      country: trip?.address?.country || 'GB', // Default to GB if country is not set
      date: DateTime.fromISO(endDate, { zone: 'utc' }).toISODate(),
      description: trip?.title || 'Trip with Skyhook',
      currencies: currency,
      total: parseInt(values.prices.payment),
      // Authentication
      booking_auth: authorisation.trustMyTravelAuthorisationCode.code,
      // Lead Traveller
      firstname: values.address.givenName,
      surname: values.address.familyName,
      email: user.email,
      // Payment details
      payee_name: `${values.address.givenName} ${values.address.familyName}`,
      payee_email: user.email,
      payee_address: values.address.addressLine1,
      payee_city: values.address.locality,
      payee_country: values.address.country,
      // Note some regions don't have a postcode (e.g. most of Africa), so we have a default here. TMT have confirmed
      // that setting n/a should be sufficient to pass validation.
      payee_postcode: values.address.postalCode || 'n/a',
    },
  };

  if (env !== 'prod') (res as any).environment = 'test';

  return res;
}

export interface FormatModalOptionsProps {
  group: Pick<LegacyGroup, 'guest_price' | 'id' | 'end'>;
  trip: { address: { country: string } };
  user: Pick<LegacyUser, 'id' | 'email'>;
  authorisation: TrustMyTravelAuthorisationCodeQuery;
  values: {
    prices: { payment: string };
    address: TransactionAddressInput;
    turnstileToken: string;
  };
  env: 'prod' | 'dev';
}

/**
 * Setup and launch the TMT payment modal
 */
export default async function setupModal({
  tmtAuthLazyQuery,
  currency,
  endDate,
  trip,
  user,
  values,
}: SetupModalProps): Promise<TMTModal> {
  // Get the TMT Booking Authorisation
  // TMT requires a strange auth flow whereby an auth string must be generated before each transaction. It adds nothing
  // to security, so we just expose this with a public query. Details at
  // https://demo.trustmytravel.com/modal/docs3.0.0/user-docs-3.0.0/auth/
  const authorisationResponse = await tmtAuthLazyQuery({
    variables: {
      price: {
        amountZD: values.prices.payment,
        currency,
      },
      turnstileToken: values.turnstileToken,
    },
  });

  const modalOptions = formatModalOptions({
    currency,
    endDate,
    trip,
    user,
    authorisation: authorisationResponse.data,
    values,
  });

  // Return the TMT Modal (automatically opens it)
  const modal = new (window as any).tmtPaymentModalSdk(
    modalOptions,
  ) as TMTModal;

  return modal;
}

export interface SetupModalProps {
  tmtAuthLazyQuery: LazyQueryExecFunction<
    TrustMyTravelAuthorisationCodeQuery,
    QueryTrustMyTravelAuthorisationCodeArgs
  >;
  currency: Currency;
  /** UTC end date */
  endDate: string;
  trip: { address: { country: string } } & Pick<Trip, 'title'>;
  user: Pick<LegacyUser, 'id' | 'email'>;
  values: {
    prices: { payment: number };
    address: TransactionAddressInput;
    turnstileToken: string;
  };
}

export interface TMTModal {
  on: (event: string, callback: (data: any) => void) => void;
  closeModal: () => void;
}

/**
 * Modal Options Interface
 *
 * Auto-generated using the sample options at https://demo.trustmytravel.com/modal/docs3.0.0/user-docs-3.0.0/options/
 */
export interface ModalOptions {
  path: string;
  environment: string;
  transactionType: string;
  disableLang: boolean;
  data: {
    booking_id: string;
    channels: number;
    country: string;
    date: string;
    currencies: string;
    total: number;
    booking_auth: string;
    firstname: string;
    surname: string;
    email: string;
    payee_name: string;
    payee_email: string;
    payee_address: string;
    payee_city: string;
    payee_country: string;
    payee_postcode: string;
  };
}
